import type { TutorKanbanStore } from './interfaces'
import type { TutorKanbanStage } from '@brighterly/lib-core-types/tutor-kanban/enums'
import type { KanbanAgent } from '@entities/task'

import { isTutorKanbanExitStage } from '../../config/tutor-kanban-exit-stage.const'

export const actions = {
  setLoading (this: TutorKanbanStore, payload: TutorKanbanStore['loading']): void {
    this.loading = payload
  },

  setTutors (this: TutorKanbanStore, payload: TutorKanbanStore['tutors']): void {
    this.tutors = payload
  },

  setAgents (this: TutorKanbanStore, payload: TutorKanbanStore['agents']): void {
    this.agents = payload
  },

  setTutor (this: TutorKanbanStore, payload: TutorKanbanStore['tutor']): void {
    this.tutor = payload
    this.savedStage = payload?.stage ?? null
    this.savedStageDetails = {
      reasons: [...(payload?.stage_details?.reasons ?? [])],
      categories: [...(payload?.stage_details?.categories ?? [])],
    }
    this.savedExitDate = payload?.exit_date ?? null
    this.finalDayConfirmOpen = false
    this.pendingStageReasons = null
    this.reasonConfirmSource = null
  },

  setTutorStage (this: TutorKanbanStore, payload: TutorKanbanStage | null): void {
    if (!this.tutor) return

    const isExitToExit = isTutorKanbanExitStage(this.tutor.stage) && isTutorKanbanExitStage(payload)

    this.tutor.stage = payload

    if (!isExitToExit) {
      this.tutor.stage_details.reasons = []
      this.tutor.stage_details.categories = []
    }

    if (!isTutorKanbanExitStage(payload)) {
      this.tutor.exit_date = this.savedExitDate
    }

    this.pendingStageReasons = null
    this.reasonConfirmSource = null
    this.isValid = true
  },

  setPendingStageReasons (this: TutorKanbanStore, payload: TutorKanbanStore['pendingStageReasons']): void {
    this.pendingStageReasons = payload
  },

  setReasonConfirmSource (this: TutorKanbanStore, payload: TutorKanbanStore['reasonConfirmSource']): void {
    this.reasonConfirmSource = payload
  },

  setReasonResetKey (this: TutorKanbanStore, payload: TutorKanbanStore['reasonResetKey']): void {
    this.reasonResetKey = payload
  },

  setTutorPriority (this: TutorKanbanStore, payload: number | null): void {
    if (!this.tutor) return
    this.tutor.kanban_priority = payload
  },

  setTutorAgent (this: TutorKanbanStore, payload: KanbanAgent | null): void {
    if (!this.tutor) return
    this.tutor.agent = payload
  },

  setTutorStageDeadline (this: TutorKanbanStore, payload: string | null): void {
    if (!this.tutor) return
    this.tutor.stage_deadline = payload
  },

  setTutorExitDate (this: TutorKanbanStore, payload: string | null): void {
    if (!this.tutor) return
    this.tutor.exit_date = payload
  },

  setFinalDayConfirmOpen (this: TutorKanbanStore, payload: TutorKanbanStore['finalDayConfirmOpen']): void {
    this.finalDayConfirmOpen = payload
  },

  setTutorStageReasons (this: TutorKanbanStore, payload: string[]): void {
    if (!this.tutor) return
    this.tutor.stage_details.reasons = payload
  },

  setTutorStageCategories (this: TutorKanbanStore, payload: string[]): void {
    if (!this.tutor) return
    this.tutor.stage_details.categories = payload
  },

  setLogs (this: TutorKanbanStore, payload: TutorKanbanStore['logs']): void {
    this.logs = payload
  },

  setLogsPagination (this: TutorKanbanStore, payload: TutorKanbanStore['logsPagination']): void {
    this.logsPagination = payload
  },

  setTimeOffs (this: TutorKanbanStore, payload: TutorKanbanStore['timeOffs']): void {
    this.timeOffs = payload
  },

  setIsValid (this: TutorKanbanStore, payload: TutorKanbanStore['isValid']): void {
    this.isValid = payload
  },
}
